import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Card, Col, Row } from 'antd';

import { MonthlyChartMonth, useGetMonthlyChartDataQuery } from './api/analyticsApiSlice';

interface MonthlyChartProps {
    from: string;
    to: string;
}

interface MonthSegment {
    category: string;
    value: number;
    color: string;
}

interface MonthBarShapeProps {
    x: number;
    y: number;
    width: number;
    height: number;
    payload: MonthlyChartMonth & { __segments: MonthSegment[]; __total: number };
}

function MonthBarShape({ x, y, width, height, payload }: MonthBarShapeProps) {
    const { __segments: segments, __total: total } = payload;

    if (!total) {
        return null;
    }

    let bottom = y + height;

    return (
        <g>
            {segments.map((segment) => {
                const segmentHeight = (segment.value / total) * height;
                const segmentY = bottom - segmentHeight;
                bottom = segmentY;

                return <rect key={segment.category} x={x} y={segmentY} width={width} height={segmentHeight} fill={segment.color} />;
            })}
        </g>
    );
}

export function MonthlyChart({ from, to }: MonthlyChartProps) {
    const { data: result } = useGetMonthlyChartDataQuery({ from, to });
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    if (!result) {
        return null;
    }

    const toggleCategory = (category: string) => {
        setSelectedCategory((prev) => (prev === category ? null : category));
    };

    const { data, metadata: { categoryToColor } } = result;
    const categories = Object.keys(categoryToColor);

    const renderLegend = () => (
        <ul style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, padding: 0, margin: 0, listStyle: 'none' }}>
            {categories.map((category) => (
                <li
                    key={category}
                    onClick={() => toggleCategory(category)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        opacity: selectedCategory && selectedCategory !== category ? 0.5 : 1,
                    }}
                >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: categoryToColor[category], display: 'inline-block' }} />
                    {category}
                </li>
            ))}
        </ul>
    );

    const chartData = data.map((month) => {
        const segments = categories
            .filter((category) => month[category] != null)
            .map((category) => ({ category, value: Number(month[category]) || 0, color: categoryToColor[category] }))
            .sort((a, b) => b.value - a.value);

        return {
            ...month,
            __segments: segments,
            __total: segments.reduce((sum, segment) => sum + segment.value, 0),
        };
    });

    return (
        <Card>
            <Row justify="center" align="middle">
                <Col xs={24}>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData} maxBarSize={30}>
                            <CartesianGrid strokeDasharray="2 6" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Legend content={renderLegend} />
                            {selectedCategory ? (
                                <Bar
                                    key={selectedCategory}
                                    dataKey={selectedCategory}
                                    fill={categoryToColor[selectedCategory]}
                                    animationDuration={500}
                                    animationEasing="ease-in-out"
                                />
                            ) : (
                                <Bar
                                    key="__total"
                                    dataKey="__total"
                                    shape={(props: any) => <MonthBarShape {...props} />}
                                    animationDuration={500}
                                    animationEasing="ease-in-out"
                                />
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                </Col>
            </Row>
        </Card>
    )
}
